import type { SaveGame } from "../types";

export type BannerSlot = "top" | "bottom" | "sidebar";

export type AdEventType = "adStarted" | "adFinished" | "adError";

export interface AdEvent {
  type: AdEventType;
  rewarded?: boolean;
  message?: string;
}

export interface PlatformCapabilities {
  ads: boolean;
  rewarded: boolean;
  banner: boolean;
  cloudSave: boolean;
  leaderboard: boolean;
}

export interface PlatformAdapter {
  init(): Promise<void>;

  getCapabilities(): PlatformCapabilities;

  gameReady(): void;
  notifyLoaded(): void;

  gameplayStart(): void;
  gameplayStop(): void;

  showInterstitialAd(): Promise<void>;
  showRewardedAd(): Promise<boolean>;
  showBanner(slot: BannerSlot): Promise<void>;
  hideBanner(): Promise<void>;
  happyTime(): void;

  onAdEvent(handler: (event: AdEvent) => void): void;

  saveProgress(state: SaveGame): Promise<void>;
  loadProgress(): Promise<SaveGame | null>;

  submitLeaderboard(score: number): Promise<void>;

  /** Platform UI locale (e.g. "ru", "en") when the SDK exposes one; null otherwise. */
  getDetectedLanguage?(): string | null;
}

const NO_CAPABILITIES: PlatformCapabilities = {
  ads: false,
  rewarded: false,
  banner: false,
  cloudSave: false,
  leaderboard: false,
};

/**
 * Development / fallback adapter. All platform features are no-ops and every
 * capability flag is false, so the UI hides ads, banners and leaderboard.
 */
export class LocalPlatformAdapter implements PlatformAdapter {
  async init(): Promise<void> {
    return;
  }

  getCapabilities(): PlatformCapabilities {
    return NO_CAPABILITIES;
  }

  gameReady(): void {
    return;
  }

  notifyLoaded(): void {
    return;
  }

  gameplayStart(): void {
    return;
  }

  gameplayStop(): void {
    return;
  }

  async showInterstitialAd(): Promise<void> {
    return;
  }

  async showRewardedAd(): Promise<boolean> {
    return false;
  }

  async showBanner(_slot: BannerSlot): Promise<void> {
    return;
  }

  async hideBanner(): Promise<void> {
    return;
  }

  happyTime(): void {
    return;
  }

  async saveProgress(_state: SaveGame): Promise<void> {
    return;
  }

  async loadProgress(): Promise<SaveGame | null> {
    return null;
  }

  async submitLeaderboard(_score: number): Promise<void> {
    return;
  }

  onAdEvent(_handler: (event: AdEvent) => void): void {
    return;
  }
}

let adapter: PlatformAdapter = new LocalPlatformAdapter();

export function getPlatformAdapter(): PlatformAdapter {
  return adapter;
}

export function setPlatformAdapter(nextAdapter: PlatformAdapter): void {
  adapter = nextAdapter;
}
